import ActionButton from "@/components/buttons/ActionButton";
import { List } from "@/components/list/List";
import Ellipsis from "@/components/views/Ellipsis";
import { tableStandardProps } from "@/utils/componentUtils";
import { formatLocaleCurrency, formatLocaleNettoPoints } from "@/utils/formatUtils";
import { tInterval } from "@/utils/translationUtils";
import { isDefined } from "@/utils/utils";
import { Alert, Card, Table } from "antd";
import { ColumnsType } from "antd/es/table";
import { useTranslation } from "react-i18next";
import { Permission } from "../../../common/security/enums";
import { AgentTypeTagWithName } from "../../agent/components/tags/AgentTypeTagWithName";
import { formatAgentIdNumber } from "../../agent/utils";
import ComponentWithPermission from "../../auth/components/ComponentWithPermission";
import { useHasPermissions, useIsCurrentAgentTypePlus } from "../../auth/hooks";
import { CalculatedCommission } from "../batch/types";

type Props = {
  calculatedCommissionsGroup: CalculatedCommission[][];
  onUpdate?: () => void;
};

export const CalculatedCommissionsGroupList = ({ calculatedCommissionsGroup, onUpdate }: Props) => {
  const { t } = useTranslation();

  const isTopAgentPlus = useIsCurrentAgentTypePlus();

  const hasReadAgentPermission = useHasPermissions(Permission.AGENT_READ);

  return (
    <List
      layout="grid"
      columns={2}
      items={calculatedCommissionsGroup}
      renderItem={(calculatedCommissions, calculatedCommissionsIndex) => {
        const hasCalculationErrors = calculatedCommissions.some((c) => !!c.errors.length);
        const hasModifiedCalculation = calculatedCommissions.some((c) => c.modified);

        const columns: ColumnsType<CalculatedCommission> = [
          {
            key: "gainer",
            title: t("commissions.batch.attrs.commission.calculatedCommissions.gainer"),
            width: 170,
            ellipsis: { showTitle: false },
            render: (_, record) => (
              <AgentTypeTagWithName
                agent={record.gainer}
                ellipsis
                showLink={hasReadAgentPermission}
                linkTarget="_blank"
                hideIdNumber
              />
            ),
          },
          {
            key: "idNumber",
            title: t("commissions.batch.attrs.commission.calculatedCommissions.idNumber"),
            width: 100,
            ellipsis: { showTitle: false },
            render: (_, record) => formatAgentIdNumber(record.gainer),
          },
          {
            key: "commissionAmount",
            title: t("commissions.batch.attrs.commission.calculatedCommissions.commissionAmount"),
            align: "right",
            width: 140,
            ellipsis: { showTitle: false },
            render: (_, record) =>
              isDefined(record.commissionAmount)
                ? formatLocaleCurrency(record.commissionAmount, isTopAgentPlus ? 6 : 2)
                : undefined,
          },
          {
            key: "nettoPointAmount",
            title: t("commissions.batch.attrs.commission.calculatedCommissions.nettoPointAmount"),
            align: "right",
            width: 130,
            ellipsis: { showTitle: false },
            render: (_, record) =>
              isDefined(record.nettoPointAmount)
                ? formatLocaleNettoPoints(record.nettoPointAmount, isTopAgentPlus ? 6 : 2)
                : undefined,
          },
        ];

        if (hasModifiedCalculation) {
          columns.push(
            {
              key: "modifiedBy",
              title: t("commissions.batch.attrs.commission.calculatedCommissions.modifiedBy"),
              width: 110,
              align: "center",
              ellipsis: { showTitle: false },
              render: (_, record) => <Ellipsis>{record.modifiedBy?.name}</Ellipsis>,
            },
            {
              key: "modificationReason",
              title: t("commissions.batch.attrs.commission.calculatedCommissions.modificationReason"),
              width: 120,
              align: "center",
              ellipsis: { showTitle: false },
              render: (_, record) => <Ellipsis>{record.modificationReason}</Ellipsis>,
            },
          );
        }

        if (hasCalculationErrors) {
          columns.push({
            key: "calculationErrors",
            width: 150,
            ellipsis: { showTitle: false },
            render: (_, record) =>
              !!record.errors.length && (
                <Alert
                  className="margin-left-medium margin-top-tiny margin-bottom-tiny"
                  showIcon
                  type="error"
                  title={<Ellipsis>{record.errors.flatMap((e) => e.description).join(" ")}</Ellipsis>}
                  style={{ margin: 0 }}
                />
              ),
          });
        }

        return (
          <Card
            title={tInterval(`contract.attrs.gainerRecords.gainerId_interval`, calculatedCommissionsIndex + 1)}
            extra={
              onUpdate && (
                <ComponentWithPermission permissions={[Permission.COMMISSIONS_MANAGE]}>
                  <ActionButton icon="edit" color="green" variant="solid" onClick={() => onUpdate()}>
                    {t("common.edit")}
                  </ActionButton>
                </ComponentWithPermission>
              )
            }
          >
            <Table<CalculatedCommission>
              {...tableStandardProps(true, false, "inner-table")}
              columns={columns}
              scroll={{ x: 550 }}
              dataSource={calculatedCommissions}
              bordered
              pagination={false}
            />
          </Card>
        );
      }}
    />
  );
};
